import { makeStyles, tokens } from "@fluentui/react-components";

/**
 * Shared typography styles for consistent text rendering across the app.
 * Import and use: const typography = useTypographyStyles();
 *
 * ----------------------------------------------------------------------------
 * Picking the right class
 * ----------------------------------------------------------------------------
 *
 * Headings:
 *   - pageTitle        Page H1. Prefer the Fluent <Title3> component for the
 *                      same effect with semantic HTML.
 *   - sectionTitle     Top-level section heading inside a page (e.g.
 *                      "Client Information" on Setup).
 *   - subsectionTitle  Inline / sub-section heading (e.g. "Pre-signing
 *                      Checklist" on Review). Same size as sectionTitle but
 *                      reserved for inline use inside a tab/card so it sits
 *                      above formLabel (Base300) with clear hierarchy.
 *
 * Body text:
 *   - bodyLarge        Default body text. Same size as Fluent's defaults so
 *                      it matches Radio/Checkbox option labels.
 *   - bodySmall        Smaller / dense body text.
 *   - bodySmallStrong  Same size as bodySmall but semibold. For dense
 *                      emphasis (currency totals, small inline titles).
 *   - caption          Smaller muted/helper text. Use for hints, timestamps,
 *                      meta info, secondary labels.
 *   - sectionLabel     Small uppercase muted label that introduces a block
 *                      of detail content (e.g. "AGENT MESSAGE" / "RESULT"
 *                      headings inside Task dialogs).
 *
 * Form controls:
 *   - formLabel        Label that sits ABOVE a Fluent Radio/Checkbox group.
 *                      Same size as the Fluent control's internal label so
 *                      the question and answer text read at one size.
 *   - responseYes      Readonly "Yes" answer (semibold green).
 *   - responseNo       Readonly "No" answer (semibold red).
 *   - errorText        Inline error message (red).
 *
 * Specialised:
 *   - monospace        Code, prompts, raw cell values.
 *   - statNumber/Compact + statLabel  Dashboard stat cards.
 *   - listItemPrimary/Secondary       Two-row list items.
 *   - emptyStateTitle/Body            Empty-state messages.
 */
export const useTypographyStyles = makeStyles({
  // ------ Headings ------

  pageTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase500,
  },

  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
  },

  subsectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
  },

  // ------ Body text ------

  bodyLarge: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase300,
  },

  bodySmall: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase200,
  },

  bodySmallStrong: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase200,
  },

  caption: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
  },

  sectionLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground2,
  },

  // ------ Form controls ------

  /** Label above a Fluent Radio/Checkbox group. Base300 matches the size of
   *  the control's own internal label, so the question and the answer
   *  options render at one consistent size. */
  formLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
  },

  responseYes: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteGreenForeground1,
  },

  responseNo: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
  },

  errorText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorPaletteRedForeground1,
  },

  // ------ Specialised ------

  monospace: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },

  statNumber: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "1",
  },

  statNumberCompact: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "1",
  },

  statLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },

  listItemPrimary: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
  },

  listItemSecondary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  emptyStateTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },

  emptyStateBody: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground3,
  },
});
